/**
 * QuoteSectionCard.tsx
 *
 * The banded, collapsible block the quote editor's sections sit in — legacy's
 * Procedures / Products / Discounts / Financing Options / Totals bands.
 *
 * Not components/ui/SectionShell (the appointment and block-time modals'
 * section): that one is a controlled, uppercase text-xs header with a right-hand
 * up/down chevron on a clickable div, px-3/p-3 padding and mb-4 spacing, and it
 * has no slot for the column captions or the Add button this band carries.
 * Swapping would change how every quote section looks, so this keeps its own
 * shape: a rotating left chevron inside a real <button> with aria-expanded,
 * px-4/p-4 padding, and open state held here.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuoteSectionCardProps {
  title: string;
  /** Right-aligned column captions, as legacy prints them on the band. */
  columns?: string[];
  onAdd?: () => void;
  addLabel?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function QuoteSectionCard({
  title,
  columns = [],
  onAdd,
  addLabel = 'Add',
  defaultOpen = true,
  children,
}: QuoteSectionCardProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 bg-slate-50 px-4 py-2">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex flex-1 items-center gap-2 text-left touch-target-h"
          aria-expanded={open}
        >
          <ChevronDown
            className={cn('h-3.5 w-3.5 text-slate-400 transition-transform', !open && '-rotate-90')}
          />
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        </button>

        <div className="flex items-center gap-4 max-md:hidden">
          {columns.map((column) => (
            <span key={column} className="w-24 text-right text-xs font-medium uppercase tracking-wide text-slate-500">
              {column}
            </span>
          ))}
        </div>

        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            className="ml-2 flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 touch-target-h tap-feedback max-md:px-3 max-md:text-sm"
          >
            <Plus className="h-3.5 w-3.5" />
            {addLabel}
          </button>
        ) : null}
      </div>

      {open ? <div className="border-t border-slate-200 p-4">{children}</div> : null}
    </div>
  );
}

/** Shown in a section that has no rows yet. Says what goes here, not how. */
export function EmptySectionRow({ children }: { children: ReactNode }) {
  return <p className="py-2 text-sm italic text-slate-400">{children}</p>;
}
